import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { diffWords } from 'diff';

// Word-level diff of current note vs proposed Master Note, in its own modal so
// the Master Note preview code stays untouched. Opened from a "View diff"
// button on the preview; purely read-only. Same red/green treatment as the
// Process review panel (ProcessDiffReview): green = added, red strikethrough
// = removed. Noisy for a whole-note rewrite — the preview's semantic
// highlights remain the primary read — but useful to audit exactly what text
// survived.
export default function MasterNoteDiffModal({ currentText, proposedText, onClose }) {
  // Memoized: diffWords is O(current × proposed) and the texts never change
  // while the modal is open.
  const parts = useMemo(
    () => diffWords(currentText || '', proposedText || ''),
    [currentText, proposedText],
  );

  // Tailwind utilities in this app are scoped to `#clinic-root` (tailwind.config
  // `important: '#clinic-root'`), so the modal MUST be portaled inside that
  // element or every class on it is inert and it renders invisible. Fall back to
  // body only if the root isn't found.
  const portalTarget =
    (typeof document !== 'undefined' && document.getElementById('clinic-root')) ||
    (typeof document !== 'undefined' ? document.body : null);
  if (!portalTarget) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4">
      <div className="pointer-events-auto flex h-[min(85vh,850px)] w-[min(92vw,1100px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h2 className="text-sm font-semibold">Master Note diff</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Word-level diff: current note vs proposed Master Note.
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-100 ring-1 ring-emerald-300 dark:bg-emerald-900/50" /> added
                <span className="ml-1 inline-block h-2.5 w-2.5 rounded-sm bg-rose-100 ring-1 ring-rose-300 dark:bg-rose-900/50" /> removed
              </span>
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <pre className="flex-1 overflow-auto whitespace-pre-wrap break-words px-4 py-3 text-xs text-slate-800 dark:text-slate-200">
          {parts.map((p, i) => (
            <span
              key={i}
              className={p.added ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
                : p.removed ? 'bg-rose-100 text-rose-800 line-through dark:bg-rose-900/50 dark:text-rose-200' : ''}
            >
              {p.value}
            </span>
          ))}
        </pre>
      </div>
    </div>,
    portalTarget,
  );
}
